// packages import
import { Box, Checkbox, FormControlLabel, Grid } from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

// components import
import BillClientMenu from '../../../components/billing/billDetail/BillClientMenu'
import BillDeliveryDetailsCard from '../../../components/billing/billDetail/BillDeliveryDetailsCard'
import BillDetailHeader from '../../../components/billing/billDetail/BillDetailHeader'
import BillDetailsCard from '../../../components/billing/billDetail/BillDetailsCard'
import BillNotesCard from '../../../components/billing/billDetail/BillNotesCard'
import BillPreview from '../../../components/billing/billDetail/BillPreview'
import BillTimeline from '../../../components/billing/billDetail/BillTimeline'
import ClientBillCopyWidget from '../../../components/billing/billDetail/ClientBillCopyWidget'
import MissingClientAddressAlert from '../../../components/billing/billDetail/MissingClientAddressAlert'

// others
import { Bill, BillStatus } from '../../../../generated'
import BillTransactions from '../../../components/billing/billDetail/BillTransactions'
import { FINAL_BILL_STATUSES, VALID_BILL_DELIVERY_STATUS } from '../../../constants'
import { BillDeliveryCard } from '../cases/caseFinancials/billDeliveries'
import useFetchBillDetails from '../../../hooks/useFetchBillDetails'

const BillDetail: FC = (): JSX.Element => {
  const [showPrebill, setShowPrebill] = useState(false)
  const [refetchPayments, setRefetchPayments] = useState(new Date().getTime())
  const { id: billId } = useParams<{ id: string }>()
  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()
  const handleClientSelect = useCallback(
    (clientId: string) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set('clientId', clientId)
      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const selectedClientId = searchParams.get('clientId') || undefined
  const { bill, clientData, interestData, outstandingBillsData, refetch, refetchBillDetails } = useFetchBillDetails({
    billId: billId ? parseInt(billId) : undefined,
    selectedClientId,
    isPrebillView: showPrebill,
    includeInterest: true,
    setInitialClientId: handleClientSelect
  })

  const handleRefetch = useCallback(() => {
    refetch()
    setRefetchPayments(new Date().getTime())
  }, [refetch, setRefetchPayments])

  const { id, status, amount, amountPaid, dueDate, issueDate, case: caseData, billTimelines = [] } = bill || {}
  const { caseNumber, title, caseToExternalUserToExternalUserRole, consultMatterNumber } = caseData || {}

  const finalBillStatuses = [...FINAL_BILL_STATUSES, BillStatus.Forwarded, BillStatus.WrittenOff]
  const isFinalBillView = location.state?.isFinalBillView || (bill?.status && finalBillStatuses.includes(bill.status))
  const showPrebillView = showPrebill || !isFinalBillView
  const isSplitBill = caseData?.billing?.splitBillEnabled

  const accessTokens = useMemo(() => bill?.accessTokens || [], [bill?.accessTokens])
  const selectedAccessToken = useMemo(
    () => accessTokens.find(token => token.client?.id === selectedClientId),
    [accessTokens, selectedClientId]
  )

  useEffect(() => {
    if (selectedClientId && !selectedAccessToken && accessTokens.length > 0) {
      const firstAccessToken = accessTokens[0]
      if (firstAccessToken.client) {
        handleClientSelect(firstAccessToken.client.id)
      }
    }
  }, [selectedClientId, selectedAccessToken, accessTokens, handleClientSelect])

  return (
    <Box py={1}>
      <BillDetailHeader
        bill={bill}
        clientId={selectedAccessToken?.client?.id}
        clientPayableAmount={clientData?.payableAmount}
        onRefetch={handleRefetch}
      />

      <MissingClientAddressAlert bill={bill} />

      <Grid container spacing={5}>
        {isFinalBillView && (
          <Grid item xs={12} my={-3} display="flex" justifyContent="flex-end">
            <FormControlLabel
              control={<Checkbox checked={showPrebill} onChange={() => setShowPrebill(!showPrebill)} />}
              label={'Show Pre Bill'}
            />
          </Grid>
        )}

        <Grid item xs={12} lg={5}>
          {showPrebillView && (
            <>
              <BillDetailsCard
                bill={{
                  id,
                  status,
                  amount,
                  amountPaid,
                  dueDate,
                  issueDate,
                  case: {
                    id: caseData?.id,
                    caseNumber: caseNumber || consultMatterNumber,
                    title,
                    caseToExternalUserToExternalUserRole
                  }
                }}
              />
            </>
          )}

          {!showPrebillView && isSplitBill && accessTokens.length > 0 && (
            <BillClientMenu bill={bill} selectedClientId={selectedClientId} onClientSelect={handleClientSelect} />
          )}

          {!showPrebillView && <ClientBillCopyWidget bill={bill as Bill} onRefetch={refetchBillDetails} />}

          <BillDeliveryDetailsCard
            parties={caseData?.caseToExternalUserToExternalUserRole}
            ccEmailBillsTo={caseData?.ccEmailBillsTo}
            billId={id}
            billStatus={status}
            lastSentAt={bill?.lastSentAt}
            billTimelines={billTimelines}
            isSplitBill={isSplitBill}
            selectedClientId={selectedClientId}
            onRefetch={handleRefetch}
          />

          <BillTimeline title="Bill Timeline" billTimelines={billTimelines} showPrebillView={showPrebillView} />
          {bill?.case?.id && <BillNotesCard caseId={bill.case.id} />}
          <BillTransactions billId={bill?.id} triggerRefetch={refetchPayments} />
          {VALID_BILL_DELIVERY_STATUS.includes(bill?.status as BillStatus) && (
            <BillDeliveryCard bill={bill as Bill} refetch={refetchBillDetails} />
          )}
        </Grid>

        <Grid item xs={12} lg={7}>
          <BillPreview
            bill={bill}
            client={selectedAccessToken?.client}
            selectedAccessToken={{ token: selectedAccessToken?.token, amount: selectedAccessToken?.amount }}
            clientData={clientData}
            interestData={interestData}
            outstandingBillsData={outstandingBillsData}
            isFinalBill={!showPrebillView}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default BillDetail
